import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, clearSecureAuthStorage } from '@/integrations/supabase/client';

export type UserRole = 'site_admin' | 'owner' | 'admin' | 'read_only' | 'service_account';

interface UserProfile {
  id: string;
  user_id: string;
  account_id: string;
  display_name: string;
  role: UserRole;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
  invited_by?: string;
  account_slug?: string;
  allow_public_urls?: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, displayName: string, inviteToken?: string, organizationName?: string) => Promise<{ error: any; data?: { session: any; user: any; emailConfirmationRequired: boolean } }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUserProfile = async (userId: string, userEmail?: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          *,
          accounts!inner(slug, allow_public_urls)
        `)
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      
      // Flatten the account data into the profile
      const profile = {
        ...data,
        account_slug: data.accounts?.slug,
        allow_public_urls: data.accounts?.allow_public_urls
      };
      
      setProfile(profile);

      // Set user info in Crisp chat for better support experience
      if (typeof window !== 'undefined' && window.$crisp && window.$crisp.push) {
        try {
          window.$crisp.push(['set', 'user:email', [userEmail || '']]);
          window.$crisp.push(['set', 'user:nickname', [profile.display_name || '']]);
          window.$crisp.push(['set', 'session:data', [
            ['userId', userId],
            ['accountId', profile.account_id],
            ['userRole', profile.role],
            ['accountSlug', profile.account_slug || '']
          ]]);
        } catch (crispError) {
          console.warn('Failed to set Crisp user info:', crispError);
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setProfile(null);
    }
  };

  useEffect(() => {
    // Set up auth state listener - this is the recommended Supabase pattern
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (event === 'SIGNED_IN' && session?.user) {
          // Update last_login_at
          setTimeout(async () => {
            await supabase
              .from('user_profiles')
              .update({ last_login_at: new Date().toISOString() })
              .eq('user_id', session.user.id);
            
            await loadUserProfile(session.user.id, session.user.email);
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setProfile(null);
          clearSecureAuthStorage();
        }
        
        setLoading(false);
      }
    );

    // Check for existing session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        loadUserProfile(session.user.id, session.user.email);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) return { error };
      
      // Let onAuthStateChange handle the redirect
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const signUp = async (email: string, password: string, displayName: string, inviteToken?: string, organizationName?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName,
            invite_token: inviteToken,
            organization_name: organizationName,
          },
        },
      });

      if (error) return { error };
      
      // When email confirmation is disabled, we should get a session immediately
      // If no session is returned, it means email confirmation is still required
      return { 
        error: null, 
        data: {
          session: data.session,
          user: data.user,
          emailConfirmationRequired: !data.session
        }
      };
    } catch (error) {
      return { error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      // Get the current origin and ensure it's correct
      let redirectUrl = `${window.location.origin}/reset-password`;
      
      // For development, ensure we're using the correct localhost port
      if (window.location.hostname === 'localhost') {
        redirectUrl = `${window.location.protocol}//${window.location.host}/reset-password`;
      }
      
      console.log('Password reset redirect URL:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error('Password reset error details:', {
          message: error.message,
          status: error.status,
          details: error
        });
      }

      return { error };
    } catch (error) {
      console.error('Password reset exception:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
      }
    } catch (error) {
      console.error('Error signing out:', error);
      clearSecureAuthStorage();
    }
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signIn,
    signUp,
    resetPassword,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};